import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { VideoApprovalModal } from './modals/VideoApprovalModal';
import { ProductApprovalModal } from './modals/ProductApprovalModal';
import { ImageApprovalModal } from './modals/ImageApprovalModal';

/**
 * ApprovalModalV3
 *
 * Router component that delegates to specialized modal components
 * based on the content type (image, video, or product).
 *
 * This is the entry point for the V3 modal system. It maintains
 * the same props interface as V2 for backward compatibility.
 *
 * Content Type Detection:
 * - Video: item.type === 'video' OR item.video_id exists
 * - Product: item.type === 'product' OR item.proposal_id exists
 * - Image: Default (everything else)
 *
 * V3 Architecture Benefits:
 * - Each content type has its own dedicated modal (~200-400 lines each)
 * - Shared logic extracted into custom hooks
 * - Reusable UI sections (ModalActionFooter, DeleteAccordion)
 * - Easy to test, maintain, and extend
 */
export function ApprovalModalV3({
  item,
  isOpen,
  onClose,
  onApprove,
  onUpdate,
  onRejectImage,
  onApproveVideo,
  onDeleteVideo,
  onApproveProduct,
  onRejectProduct,
}) {
  /**
   * Determine content type from item properties
   */
  const contentType = useMemo(() => {
    if (!item) return null;
    if (item.type === 'video' || item.video_id) return 'video';
    if (item.type === 'product' || item.proposal_id) return 'product';
    return 'image';
  }, [item]);

  // Don't render if no item
  if (!item) return null;

  // Route to specialized modal based on content type
  switch (contentType) {
    case 'video':
      return (
        <VideoApprovalModal
          item={item}
          isOpen={isOpen}
          onClose={onClose}
          onApproveVideo={onApproveVideo}
          onDeleteVideo={onDeleteVideo}
        />
      );

    case 'product':
      return (
        <ProductApprovalModal
          item={item}
          isOpen={isOpen}
          onClose={onClose}
          onApproveProduct={onApproveProduct}
          onRejectProduct={onRejectProduct}
          onUpdate={onUpdate}
        />
      );

    case 'image':
    default:
      return (
        <ImageApprovalModal
          item={item}
          isOpen={isOpen}
          onClose={onClose}
          onApprove={onApprove}
          onUpdate={onUpdate}
          onRejectImage={onRejectImage}
        />
      );
  }
}

ApprovalModalV3.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.oneOf(['image', 'video', 'product']),
    // Image fields
    image_id: PropTypes.string,
    // Video fields
    video_id: PropTypes.string,
    // Product fields
    proposal_id: PropTypes.string,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Image callbacks
  onApprove: PropTypes.func,
  onUpdate: PropTypes.func,
  onRejectImage: PropTypes.func,
  // Video callbacks
  onApproveVideo: PropTypes.func,
  onDeleteVideo: PropTypes.func,
  // Product callbacks
  onApproveProduct: PropTypes.func,
  onRejectProduct: PropTypes.func,
};

ApprovalModalV3.defaultProps = {
  onApprove: () => {},
  onUpdate: () => {},
  onRejectImage: () => {},
  onApproveVideo: () => {},
  onDeleteVideo: () => {},
  onApproveProduct: () => {},
  onRejectProduct: () => {},
};

export default ApprovalModalV3;
